import React from 'react'
import { Button, DatePicker, message, Select, Space, Switch } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import Text from '@hixme-ui/text'
import { CensusClientFilter } from '../../types/census-client-filter'
import { useCensusV2Clients } from '../../hooks/use-census-v2-clients'

const { RangePicker } = DatePicker

export interface CensusReviewDataFilterProps {
  filter?: CensusClientFilter
  onFilterChange: (filter: CensusClientFilter) => void
  className?: string
}

/**
 * Combined filter component for census review data with toggle to show/hide test companies,
 * a searchable company select, and a date range picker.
 *
 * @param filter - Current filter state
 * @param onFilterChange - Callback when filter changes
 */
const CensusReviewDataFilter: React.FC<CensusReviewDataFilterProps> = ({
  filter,
  onFilterChange,
  className,
}) => {
  const { clients, error } = useCensusV2Clients()
  if (error) {
    const msg = `Error loading companies: ${error.message}`
    console.error(msg)
    message.error(msg, 5)
  }

  const showCompanySearch = clients.length > 1

  const selectedClientId = filter?.selectedClient?.id
  const isSelectedClientInFilteredList = clients.some(
    (c) => c.id === selectedClientId
  )

  const handleCompanyChange = (clientId: string | undefined) => {
    const selectedClient = clientId
      ? clients.find((c) => c.id === clientId)
      : undefined
    onFilterChange({ ...filter, selectedClient })
  }

  const handleToggle = (checked: boolean) => {
    onFilterChange({ ...filter, includeTestCompanies: checked })
  }

  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1]) {
      onFilterChange({
        ...filter,
        dateFrom: dates[0].format('YYYY-MM-DD'),
        dateTo: dates[1].format('YYYY-MM-DD'),
      })
    } else {
      onFilterChange({
        ...filter,
        dateFrom: undefined,
        dateTo: undefined,
      })
    }
  }

  const handleClearFilters = () => {
    onFilterChange({
      includeTestCompanies: filter?.includeTestCompanies ?? false,
      selectedClient: undefined,
      dateFrom: undefined,
      dateTo: undefined,
    })
  }

  const hasActiveFilters = Boolean(
    filter?.selectedClient || filter?.dateFrom || filter?.dateTo
  )

  const dateRangeValue: [Dayjs, Dayjs] | null =
    filter?.dateFrom && filter?.dateTo
      ? [dayjs(filter.dateFrom).local(), dayjs(filter.dateTo).local()]
      : null

  return (
    <Space direction="vertical" align="end" className={className}>
      <Space align="center">
        <Switch
          checked={filter?.includeTestCompanies ?? false}
          onChange={handleToggle}
          size="default"
        />
        <Text style={{ fontSize: '16px', marginRight: '10px' }}>
          Include test companies
        </Text>
      </Space>

      <Space
        align="center"
        wrap
        style={{ width: '100%', justifyContent: 'flex-end' }}
      >
        <Button
          type="link"
          onClick={handleClearFilters}
          style={{ visibility: hasActiveFilters ? 'visible' : 'hidden' }}
        >
          Clear filters
        </Button>

        {showCompanySearch && (
          <Select
            showSearch
            allowClear
            placeholder="Search company"
            style={{ minWidth: 300, width: '100%' }}
            optionFilterProp="label"
            value={
              isSelectedClientInFilteredList ? selectedClientId : undefined
            }
            onChange={handleCompanyChange}
            options={clients.map((client) => ({
              value: client.id,
              label: client.name,
            }))}
          />
        )}

        <RangePicker
          placeholder={['Start Date', 'End Date']}
          value={dateRangeValue}
          onChange={handleDateRangeChange}
          format="YYYY-MM-DD"
          allowClear
          style={{ minWidth: 240 }}
        />
      </Space>
    </Space>
  )
}

export default CensusReviewDataFilter
